import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Eye, EyeOff, ShieldCheck, TrendingUp, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(email.trim(), password);
      navigate("/dashboard");
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || "Unable to sign in");
    } finally { setLoading(false); }
  };

  return (
    <div className="flex min-h-screen">
      {/* Left brand panel */}
      <div className="relative hidden w-1/2 flex-col justify-between overflow-hidden p-12 lg:flex" style={{ backgroundColor: "#0C1929" }}>
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #0057A8 0, transparent 40%), radial-gradient(circle at 80% 70%, #E31E24 0, transparent 40%)" }} />
        <div className="relative flex items-center gap-4" data-testid="login-logo">
          <img src="/dazlac-logo.png" alt="Dazlac" className="h-10 w-auto" />
          <div className="text-xs uppercase tracking-widest text-slate-400">Goldsun Paints · Flooring Systems</div>
        </div>

        <div className="relative space-y-6">
          <h1 className="text-4xl font-bold leading-tight text-white">The operating system for<br /><span style={{ color: "#E31E24" }}>industrial flooring</span> sales.</h1>
          <p className="max-w-md text-slate-300">Track every lead from first enquiry to project handover. Built for epoxy, PU and specialist coating businesses.</p>
          <div className="grid grid-cols-1 gap-3 pt-2">
            {[[TrendingUp, "Live pipeline & win-rate analytics"], [Zap, "Fast lead capture from the field"], [ShieldCheck, "Role-based access for your sales team"]].map(([Icon, t], i) => (
              <div key={i} className="flex items-center gap-3 text-sm text-slate-200">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/5"><Icon className="h-4 w-4 text-white" /></div>{t}
              </div>
            ))}
          </div>
        </div>
        <div className="relative text-xs text-slate-500">© {new Date().getFullYear()} Goldsun Paints. All rights reserved.</div>
      </div>

      {/* Right form */}
      <div className="flex w-full items-center justify-center bg-[#F5F7FA] px-6 lg:w-1/2">
        <div className="w-full max-w-sm">
          <div className="mb-8 lg:hidden" data-testid="login-logo-mobile">
            <img src="/dazlac-logo.png" alt="Dazlac" className="h-10 w-auto" />
          </div>
          <h2 className="text-2xl font-bold tracking-tight text-slate-900">Welcome back</h2>
          <p className="mt-1 text-sm text-slate-500">Sign in to your Dazlac sales workspace.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-600">Email</Label>
              <Input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@dazlac.com" className="h-11" />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold text-slate-600">Password</Label>
                <Link to="/forgot-password" data-testid="login-forgot-link" className="text-xs font-medium text-[#0057A8] hover:underline">Forgot password?</Link>
              </div>
              <div className="relative">
                <Input data-testid="login-password" type={show ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="h-11 pr-10" />
                <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            {error && <div data-testid="login-error" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{error}</div>}
            <Button data-testid="login-submit" type="submit" disabled={loading} className="h-11 w-full text-sm font-semibold">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>
          <p className="mt-6 text-center text-xs text-slate-400">Accounts are provisioned by your administrator.</p>
        </div>
      </div>
    </div>
  );
}
